import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import familyAPI from "../../../services/family";

export const OBSERVATION_RESPONSE_MAX_LENGTH = 2000;

export const formatFamilyObservationDate = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Paris",
  });
};

const approverLabel = (observation) => {
  if (observation?.approved_by?.name) {
    return observation.approved_by.name;
  }
  switch (observation?.approved_by_role) {
    case "FATHER":
    case "MOTHER":
      return "un parent";
    case "STUDENT":
      return "l'élève";
    default:
      return "";
  }
};

const FamilyStudentObservationsModal = forwardRef(({ accessToken, students, onApproved }, ref) => {
  const toast = useRef(null);
  const [visible, setVisible] = useState(false);
  const [studentId, setStudentId] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [localApprovals, setLocalApprovals] = useState({});
  const [responses, setResponses] = useState({});

  useImperativeHandle(ref, () => ({
    show: (id) => {
      const target = (students || []).find((item) => item?.id === id);
      const list = target?.observations || [];
      const firstPending = list.findIndex((item) => !item?.approved);
      setStudentId(id);
      setLocalApprovals({});
      setResponses({});
      setActiveIndex(firstPending >= 0 ? firstPending : null);
      setVisible(true);
    },
    hide: () => setVisible(false),
  }));

  const student = (students || []).find((item) => item?.id === studentId);
  const observations = (student?.observations || []).map((item) =>
    localApprovals[item.id] ? { ...item, ...localApprovals[item.id] } : item
  );
  const studentName = [student?.firstname, student?.lastname].filter(Boolean).join(" ");

  const approve = (observation) => {
    setApprovingId(observation.id);
    familyAPI
      .ApproveObservation(accessToken, observation.id, (responses[observation.id] || "").trim())
      .then((res) => {
        const updated = res?.data || {};
        setLocalApprovals((prev) => ({
          ...prev,
          [observation.id]: { ...updated, approved: true, can_approve: false },
        }));
        toast.current?.show({ severity: "success", summary: "Prise de connaissance confirmée", detail: "Merci, l'école en est informée." });
        onApproved?.(observation, updated);
      })
      .catch((err) => {
        toast.current?.show({ severity: "error", summary: "Erreur", detail: err.message });
      })
      .finally(() => setApprovingId(null));
  };

  const tabHeader = (observation) => (
    <div className="flex align-items-center justify-content-between gap-2 w-full flex-wrap">
      <span className="flex align-items-center gap-2">
        <i className="pi pi-comment"></i>
        <span>Observation du {formatFamilyObservationDate(observation?.issued_at)}</span>
      </span>
      {observation?.approved ? (
        <Tag severity="success" icon="pi pi-check" value="Lue" />
      ) : (
        <Tag severity="warning" icon="pi pi-clock" value="À lire" />
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header={studentName ? `Observations — ${studentName}` : "Observations"}
        visible={visible}
        style={{ width: "50vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        modal
        onHide={() => setVisible(false)}
        footer={<Button label="Fermer" icon="pi pi-times" className="p-button-text" onClick={() => setVisible(false)} />}
      >
        {observations.length === 0 ? (
          <div className="text-center text-500 py-4">Aucune observation pour le moment.</div>
        ) : (
          <Accordion activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
            {observations.map((observation) => (
              <AccordionTab key={observation.id} header={tabHeader(observation)}>
                <div
                  className="border-round p-3"
                  style={{
                    background: "#eef7f9",
                    border: "1px solid #c9e4ea",
                    borderLeft: "5px solid #4599ac",
                  }}
                  data-testid={`family-observation-message-${observation.id}`}
                >
                  <div
                    className="text-xs font-bold uppercase mb-2"
                    style={{ color: "#2f6f7e", letterSpacing: "0.5px" }}
                  >
                    Message de l'observation
                  </div>
                  <div className="line-height-3 text-900" style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
                    {observation?.message}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-top-1 border-200 flex flex-column gap-2">
                  {observation?.approved ? (
                    <>
                      <span className="text-sm text-600">
                        <i className="pi pi-check-circle text-green-500 mr-2"></i>
                        Prise de connaissance confirmée le {formatFamilyObservationDate(observation?.approved_at)}
                        {approverLabel(observation) ? ` par ${approverLabel(observation)}` : ""}
                      </span>
                      {observation?.approval_response && (
                        <div className="surface-50 border-round p-2 text-sm">
                          <div className="font-medium mb-1">Réponse envoyée à l'école</div>
                          <div style={{ whiteSpace: "pre-line" }}>{observation.approval_response}</div>
                        </div>
                      )}
                    </>
                  ) : observation?.can_approve ? (
                    <>
                      <span className="text-sm text-600">Merci de confirmer que vous avez pris connaissance de cette observation.</span>
                      <label htmlFor={`observation-response-${observation.id}`} className="text-sm font-medium">
                        Votre réponse (facultatif)
                      </label>
                      <InputTextarea
                        id={`observation-response-${observation.id}`}
                        value={responses[observation.id] || ""}
                        onChange={(e) => setResponses((prev) => ({ ...prev, [observation.id]: e.target.value }))}
                        rows={3}
                        autoResize
                        maxLength={OBSERVATION_RESPONSE_MAX_LENGTH}
                        placeholder="Vous pouvez laisser un message à l'école..."
                        disabled={!!approvingId}
                        className="w-full"
                      />
                      <div className="flex align-items-center justify-content-between gap-2">
                        <small className="text-500">
                          {(responses[observation.id] || "").length} / {OBSERVATION_RESPONSE_MAX_LENGTH}
                        </small>
                        <Button
                          label="J'ai pris connaissance"
                          icon="pi pi-check"
                          size="small"
                          loading={approvingId === observation.id}
                          disabled={!!approvingId}
                          onClick={() => approve(observation)}
                        />
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-600">En attente de la confirmation de lecture par un parent.</span>
                  )}
                </div>
              </AccordionTab>
            ))}
          </Accordion>
        )}
      </Dialog>
    </>
  );
});

export default FamilyStudentObservationsModal;
